import { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Search, Edit, UserCog, KeyRound, Eye, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

export default function CoachManagement() {
    const [coaches, setCoaches] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [editingCoach, setEditingCoach] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", password: "", organization: "", designation: "" });
    const { impersonate } = useAuth();
    const navigate = useNavigate();

    const fetchCoaches = async () => {
        try {
            const { data } = await api.get("/admin/coaches");
            setCoaches(data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchCoaches(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredCoaches = coaches.filter(c => {
        const q = search.toLowerCase();
        return !q || c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.organization?.toLowerCase().includes(q);
    });

    const openCreate = () => {
        setEditingCoach(null);
        setForm({ name: "", email: "", password: "", organization: "", designation: "" });
        setShowDialog(true);
    };

    const openEdit = (coach) => {
        setEditingCoach(coach);
        setForm({ name: coach.name, email: coach.email, password: "", organization: coach.organization || "", designation: coach.designation || "" });
        setShowDialog(true);
    };

    const handleSubmit = async () => {
        try {
            if (editingCoach) {
                const update = { name: form.name, organization: form.organization, designation: form.designation };
                if (form.password) update.password = form.password;
                await api.put(`/admin/coaches/${editingCoach.id}`, update);
                toast.success("Coach updated");
            } else {
                await api.post("/admin/coaches", form);
                toast.success("Coach created");
            }
            setShowDialog(false);
            fetchCoaches();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const toggleStatus = async (coach) => {
        const newStatus = coach.status === "active" ? "inactive" : "active";
        try {
            await api.put(`/admin/coaches/${coach.id}`, { status: newStatus });
            toast.success(`Coach ${newStatus === "active" ? "activated" : "deactivated"}`);
            fetchCoaches();
        } catch (err) { toast.error("Failed to update status"); }
    };

    const generateResetLink = async (coach) => {
        try {
            const { data } = await api.post(`/admin/generate-reset-link/${coach.id}`);
            await navigator.clipboard.writeText(data.link);
            toast.success("Password reset link copied!", {
                description: `For ${data.email} — expires in ${data.expires_in}`,
                duration: 8000
            });
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        </div>
    );

    return (
        <div data-testid="coach-management" className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Outfit" }}>Coach Management</h1>
                    <p className="text-sm text-muted-foreground mt-1">{coaches.length} coaches registered</p>
                </div>
                <Button data-testid="create-coach-btn" onClick={openCreate} style={{ backgroundColor: "#4A7AB5" }} className="gap-2">
                    <Plus className="h-4 w-4" /> Add Coach
                </Button>
            </div>

            <Card className="border-border bg-card">
                <CardHeader className="pb-3">
                    <div className="relative w-full sm:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input data-testid="coach-search" placeholder="Search coaches..." value={search}
                            onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-background border-border" />
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-border">
                                    <TableHead className="text-muted-foreground">Name</TableHead>
                                    <TableHead className="text-muted-foreground">Organization</TableHead>
                                    <TableHead className="text-muted-foreground">Designation</TableHead>
                                    <TableHead className="text-muted-foreground">Users</TableHead>
                                    <TableHead className="text-muted-foreground">Completed</TableHead>
                                    <TableHead className="text-muted-foreground">Branding</TableHead>
                                    <TableHead className="text-muted-foreground">Status</TableHead>
                                    <TableHead className="text-muted-foreground">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredCoaches.map((c) => (
                                    <TableRow key={c.id} className="border-border hover:bg-secondary/50">
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-lg bg-[#4A7AB5]/10">
                                                    <UserCog className="h-4 w-4 text-[#4A7AB5]" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-foreground text-sm">{c.name}</p>
                                                    <p className="text-xs text-muted-foreground">{c.email}</p>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{c.organization || "-"}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{c.designation || "-"}</TableCell>
                                        <TableCell className="text-sm font-medium text-foreground">{c.user_count || 0}</TableCell>
                                        <TableCell className="text-sm font-medium text-foreground">{c.completed_count || 0}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1.5">
                                                {c.has_branding_logo ? (
                                                    <ImageIcon className="h-3.5 w-3.5 text-[#4A7AB5]" />
                                                ) : null}
                                                <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                                                    {c.branding_display_name || c.name}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={c.status === "active" ? "default" : "destructive"}
                                                className={c.status === "active" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : ""}>
                                                {c.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                <Button data-testid={`edit-coach-${c.id}`} variant="ghost" size="sm" onClick={() => openEdit(c)}>
                                                    <Edit className="h-4 w-4 text-muted-foreground" />
                                                </Button>
                                                <Button data-testid={`reset-pw-coach-${c.id}`} variant="ghost" size="sm"
                                                    onClick={() => generateResetLink(c)} title="Generate password reset link"
                                                    className="text-[#F5A623] hover:text-[#F5A623]/80">
                                                    <KeyRound className="h-4 w-4" />
                                                </Button>
                                                <Button data-testid={`toggle-coach-${c.id}`} variant="ghost" size="sm" onClick={() => toggleStatus(c)}
                                                    className={c.status === "active" ? "text-destructive" : "text-emerald-400"}>
                                                    {c.status === "active" ? "Deactivate" : "Activate"}
                                                </Button>
                                                <Button data-testid={`impersonate-coach-${c.id}`} variant="ghost" size="sm"
                                                    onClick={async () => { try { await impersonate(c.id); window.location.href = "/coach"; } catch (e) { console.error("Impersonation failed:", e); } }}
                                                    title="View as this coach"
                                                    className="text-amber-500 hover:text-amber-600">
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="bg-card border-border">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: "Outfit" }}>
                            {editingCoach ? "Edit Coach" : "Create New Coach"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Full Name</Label>
                            <Input data-testid="coach-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="bg-background border-border" />
                        </div>
                        {!editingCoach && (
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input data-testid="coach-email-input" type="email" value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-background border-border" />
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label>{editingCoach ? "New Password (leave blank to keep)" : "Password"}</Label>
                            <Input data-testid="coach-password-input" type="password" value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-background border-border" />
                        </div>
                        <div className="space-y-2">
                            <Label>Organization</Label>
                            <Input data-testid="coach-org-input" value={form.organization}
                                onChange={(e) => setForm({ ...form, organization: e.target.value })} className="bg-background border-border" />
                        </div>
                        <div className="space-y-2">
                            <Label>Designation</Label>
                            <Input data-testid="coach-desig-input" value={form.designation}
                                onChange={(e) => setForm({ ...form, designation: e.target.value })} className="bg-background border-border" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                        <Button data-testid="coach-form-submit" onClick={handleSubmit} style={{ backgroundColor: "#4A7AB5" }}>
                            {editingCoach ? "Update" : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
